// @LTG-ID: contact-step-name
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: source `Contact.html` lines 707-781 (v4)
// @CHANGED: 2026-05 session_006 (CHG-0083-M-A1)
// @OWNER: web
//
// CHG-0083-M-A1 — Step 1: Name.
//
// Two-column grid (first + last) on desktop, single-column on phone.
// Inline pill toggle ("Add a middle name") that reveals a third field.
// autoFocus on the first field. Enter key advances when valid (both filled).
// "Continue →" button disabled until valid.
//
// ARIA (OQ-V4-6 proactive hooks):
//   - aria-required on first + last (middle is optional)
//   - aria-invalid driven by validator result + touched state
//   - aria-describedby points to per-field error region; error region has role="alert"
//
// Validation gating: advance only when ALL visible fields are { ok: true }.

import { useState } from "react";
import { validateName } from "./validators";

export const StepName = ({ data, setData, onNext }) => {
    const [showMiddle, setShowMiddle] = useState(!!data.middleName);
    const [touched, setTouched] = useState({});

    const firstCheck = validateName(data.firstName, "first name");
    const lastCheck = validateName(data.lastName, "last name");
    const middleCheck =
        showMiddle && data.middleName
            ? validateName(data.middleName, "middle name")
            : { ok: true };
    const valid = firstCheck.ok && lastCheck.ok && middleCheck.ok;

    const tryNext = () => {
        setTouched({ firstName: true, lastName: true, middleName: true });
        if (valid) onNext();
    };

    const showFirstErr = touched.firstName && !firstCheck.ok;
    const showLastErr = touched.lastName && !lastCheck.ok;
    const showMiddleErr =
        touched.middleName && data.middleName && !middleCheck.ok;

    return (
        <section
            className="ltgc-step"
            data-testid="contact-step-name"
            aria-labelledby="ltgc-step-name-heading"
        >
            <span className="ltgc-step-greeting">
                Step 1 · Who&apos;s reaching out
            </span>
            <h2 id="ltgc-step-name-heading">
                Let&apos;s start with <em>your name</em>.
            </h2>
            <p className="ltgc-sub">
                We use it once. After this question, we&apos;ll address you by
                first name for the rest of the form.
            </p>

            <div className="ltgc-field-row">
                <div className="ltgc-field">
                    <label
                        className="ltgc-field-label"
                        htmlFor="ltgc-input-first-name"
                    >
                        First name
                    </label>
                    <input
                        id="ltgc-input-first-name"
                        className={
                            "ltgc-field-input " +
                            (showFirstErr ? "is-error" : "")
                        }
                        type="text"
                        autoFocus
                        placeholder="Sarah"
                        autoComplete="given-name"
                        value={data.firstName}
                        aria-required="true"
                        aria-invalid={showFirstErr ? "true" : "false"}
                        aria-describedby={
                            showFirstErr
                                ? "ltgc-input-first-name-error"
                                : undefined
                        }
                        data-testid="contact-input-first-name"
                        onChange={(e) =>
                            setData({ ...data, firstName: e.target.value })
                        }
                        onBlur={() =>
                            setTouched((t) => ({ ...t, firstName: true }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter") tryNext();
                        }}
                    />
                    {showFirstErr && (
                        <span
                            id="ltgc-input-first-name-error"
                            className="ltgc-field-error"
                            role="alert"
                            data-testid="contact-error-first-name"
                        >
                            {firstCheck.msg}
                        </span>
                    )}
                </div>
                <div className="ltgc-field">
                    <label
                        className="ltgc-field-label"
                        htmlFor="ltgc-input-last-name"
                    >
                        Last name
                    </label>
                    <input
                        id="ltgc-input-last-name"
                        className={
                            "ltgc-field-input " +
                            (showLastErr ? "is-error" : "")
                        }
                        type="text"
                        placeholder="Chen"
                        autoComplete="family-name"
                        value={data.lastName}
                        aria-required="true"
                        aria-invalid={showLastErr ? "true" : "false"}
                        aria-describedby={
                            showLastErr
                                ? "ltgc-input-last-name-error"
                                : undefined
                        }
                        data-testid="contact-input-last-name"
                        onChange={(e) =>
                            setData({ ...data, lastName: e.target.value })
                        }
                        onBlur={() =>
                            setTouched((t) => ({ ...t, lastName: true }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter") tryNext();
                        }}
                    />
                    {showLastErr && (
                        <span
                            id="ltgc-input-last-name-error"
                            className="ltgc-field-error"
                            role="alert"
                            data-testid="contact-error-last-name"
                        >
                            {lastCheck.msg}
                        </span>
                    )}
                </div>
            </div>

            <button
                type="button"
                className="ltgc-toggle-row"
                onClick={() => setShowMiddle((v) => !v)}
                aria-pressed={showMiddle}
                data-testid="contact-toggle-middle-name"
            >
                <span
                    className={
                        "ltgc-toggle-pill " + (showMiddle ? "is-on" : "")
                    }
                    aria-hidden="true"
                />
                Add a middle name
            </button>

            {showMiddle && (
                <div className="ltgc-field" style={{ marginTop: 8 }}>
                    <label
                        className="ltgc-field-label"
                        htmlFor="ltgc-input-middle-name"
                    >
                        Middle name
                    </label>
                    <input
                        id="ltgc-input-middle-name"
                        className={
                            "ltgc-field-input " +
                            (showMiddleErr ? "is-error" : "")
                        }
                        type="text"
                        placeholder="Michelle"
                        autoComplete="additional-name"
                        value={data.middleName || ""}
                        aria-required="false"
                        aria-invalid={showMiddleErr ? "true" : "false"}
                        aria-describedby={
                            showMiddleErr
                                ? "ltgc-input-middle-name-error"
                                : undefined
                        }
                        data-testid="contact-input-middle-name"
                        onChange={(e) =>
                            setData({ ...data, middleName: e.target.value })
                        }
                        onBlur={() =>
                            setTouched((t) => ({ ...t, middleName: true }))
                        }
                    />
                    {showMiddleErr && (
                        <span
                            id="ltgc-input-middle-name-error"
                            className="ltgc-field-error"
                            role="alert"
                            data-testid="contact-error-middle-name"
                        >
                            {middleCheck.msg}
                        </span>
                    )}
                </div>
            )}

            <div className="ltgc-step-nav">
                <span />
                <button
                    type="button"
                    className="ltgc-btn ltgc-btn-primary"
                    disabled={!valid}
                    onClick={tryNext}
                    data-testid="contact-step-name-continue"
                >
                    Continue <span className="ltgc-arrow">→</span>
                </button>
            </div>
        </section>
    );
};

export default StepName;
